import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Lavoro, Preventivo, EstimateLevel } from '../../types';
import { formatCurrency, getPrice, isAttiva } from '../../utils/pricing';
import { useDeletePreventivo, useUpdatePreventivo } from '../../hooks';

interface LavoroCardProps {
  lavoro: Lavoro;
  estimateLevel: EstimateLevel;
  onStimaAI: () => void;
  onUploadQuote: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onViewCme: () => void;
  onToggleAttivo: () => void;
  projectId: string;
}

export function LavoroCard({
  lavoro,
  estimateLevel,
  onStimaAI,
  onUploadQuote,
  onEdit,
  onDelete,
  onViewCme,
  onToggleAttivo,
  projectId,
}: LavoroCardProps) {
  const [expanded, setExpanded] = useState(false);
  const preventivi = lavoro.preventivi || [];
  const deletePreventivo = useDeletePreventivo();
  const updatePreventivo = useUpdatePreventivo();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lavoro.id,
  });

  // `attivo` è assente sui record precedenti alla migration: assenza = attiva.
  const lavorazioneAttiva = lavoro.attivo !== false;

  // Le voci disattivate non contano, come nel CME e nei documenti generati.
  const totale = preventivi
    .filter(isAttiva)
    .reduce((sum, p) => sum + getPrice(p, estimateLevel), 0);
  const budget = lavoro.budgetTotale ? parseFloat(lavoro.budgetTotale) : null;
  const overBudget = budget !== null && totale > budget;

  const handleDeletePreventivo = (id: string) => {
    if (window.confirm('Eliminare questa voce?')) {
      deletePreventivo.mutate({ id, projectId });
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ costoMin: '', costoMed: '', costoMax: '' });

  const startEdit = (p: Preventivo) => {
    setEditingId(p.id);
    setEditValues({
      costoMin: p.costoMin,
      costoMed: p.costoMed,
      costoMax: p.costoMax,
    });
  };

  const saveEdit = (p: Preventivo) => {
    updatePreventivo.mutate(
      {
        id: p.id,
        projectId,
        data: {
          costoMin: editValues.costoMin,
          costoMed: editValues.costoMed,
          costoMax: editValues.costoMax,
        },
      },
      { onSuccess: () => setEditingId(null) }
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border rounded-xl overflow-hidden transition-colors ${
        isDragging ? 'opacity-50 shadow-lg' : ''
      } ${
        lavorazioneAttiva
          ? 'border-gray-200 bg-white'
          : 'border-dashed border-gray-300 bg-gray-50'
      }`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="p-1 -ml-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none shrink-0"
            title="Trascina per riordinare"
            aria-label={`Sposta ${lavoro.nome}`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 4a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm6-10a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
          </button>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="min-w-0">
            <h3
              className={`text-base font-semibold truncate ${
                lavorazioneAttiva ? 'text-gray-900' : 'text-gray-500'
              }`}
            >
              {lavoro.nome}
              {!lavorazioneAttiva && (
                <span className="ml-2 text-xs font-normal bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full align-middle">
                  sospesa
                </span>
              )}
            </h3>
            {lavoro.descrizione && (
              <p className="text-sm text-gray-500 truncate">{lavoro.descrizione}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          <span className="text-xs text-gray-500">
            {preventivi.length} voc{preventivi.length === 1 ? 'e' : 'i'}
          </span>
          {budget !== null && lavorazioneAttiva && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              overBudget
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {overBudget ? '↑' : '✓'} Budget {formatCurrency(budget)}
            </span>
          )}
          <span
            className={`text-base font-bold currency ${
              !lavorazioneAttiva
                ? 'text-gray-400 line-through'
                : overBudget
                  ? 'text-red-600'
                  : 'text-primary-600'
            }`}
          >
            {formatCurrency(totale)}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleAttivo(); }}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
              lavorazioneAttiva ? 'bg-primary-600' : 'bg-gray-300'
            }`}
            title={lavorazioneAttiva ? 'Sospendi: esce dai totali' : 'Riattiva: rientra nei totali'}
            role="switch"
            aria-checked={lavorazioneAttiva}
            aria-label={`${lavorazioneAttiva ? 'Sospendi' : 'Riattiva'} ${lavoro.nome}`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                lavorazioneAttiva ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-100">
          {/* Actions */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); onUploadQuote(); }}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Carica Preventivo
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onStimaAI(); }}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Stima AI
            </button>
            {preventivi.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewCme(); }}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Vedi CME
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Modifica lavoro"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
              title="Elimina lavoro"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          {/* Preventivi list */}
          {preventivi.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Nessuna voce di costo. Usa "Carica Preventivo" o "Stima AI" per aggiungere voci.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {preventivi.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {p.categoria}
                      </span>
                      <span className="text-sm text-gray-900 truncate">{p.voce}</span>
                    </div>
                    {p.note && <p className="text-xs text-gray-500 mt-0.5 truncate">{p.note}</p>}
                  </div>
                  {editingId === p.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editValues.costoMin}
                        onChange={(e) => setEditValues(v => ({ ...v, costoMin: e.target.value }))}
                        className="w-20 text-xs py-1"
                        step="0.01"
                        min="0"
                      />
                      <input
                        type="number"
                        value={editValues.costoMed}
                        onChange={(e) => setEditValues(v => ({ ...v, costoMed: e.target.value }))}
                        className="w-20 text-xs py-1"
                        step="0.01"
                        min="0"
                      />
                      <input
                        type="number"
                        value={editValues.costoMax}
                        onChange={(e) => setEditValues(v => ({ ...v, costoMax: e.target.value }))}
                        className="w-20 text-xs py-1"
                        step="0.01"
                        min="0"
                      />
                      <button onClick={() => saveEdit(p)} className="p-1 text-primary-600 hover:bg-primary-50 rounded">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-gray-900 currency">
                        {formatCurrency(getPrice(p, estimateLevel))}
                      </span>
                      <button
                        onClick={() => startEdit(p)}
                        className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                        title="Modifica costi"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeletePreventivo(p.id)}
                        className="p-1 text-gray-400 hover:text-danger-600 rounded transition-colors"
                        title="Elimina voce"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Totals bar */}
          {preventivi.length > 0 && (
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Min: {formatCurrency(preventivi.reduce((s, p) => s + parseFloat(p.costoMin), 0))}
                {' / '}Med: {formatCurrency(preventivi.reduce((s, p) => s + parseFloat(p.costoMed), 0))}
                {' / '}Max: {formatCurrency(preventivi.reduce((s, p) => s + parseFloat(p.costoMax), 0))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
